import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useConfirmEmailVerification, useResendEmailVerification, EmailVerificationResendForm , AuthError  } from '@auth';
import { routes } from '@/shared/routes';
import Button from '@/shared/components/Button';

export default function EmailVerificationConfirmPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const confirm = useConfirmEmailVerification();
  const resend = useResendEmailVerification();
  const [email, setEmail] = useState('');
  const submittedTokens = useRef(new Set<string>());

  const confirmMutate = confirm.mutate;
  useEffect(() => {
    if (!token || submittedTokens.current.has(token)) return;
    submittedTokens.current.add(token);
    confirmMutate(
      { token },
      { onSuccess: () => navigate(routes.app.root) }
    );
  }, [token, confirmMutate, navigate]);

  const retry = () => {
    if (!token) return;
    submittedTokens.current.delete(token);
    submittedTokens.current.add(token);
    confirmMutate(
      { token },
      { onSuccess: () => navigate(routes.app.root) }
    );
  };

  const submitResend = (event: FormEvent) => {
    event.preventDefault();
    if (!email) return;
    resend.mutate({ email });
  };

  if (confirm.isError && confirm.error instanceof AuthError) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            <FormattedMessage id="auth.emailVerificationConfirm.expiredHeading" />
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            <FormattedMessage id="auth.emailVerificationConfirm.expiredBody" />
          </p>
        </div>
        <EmailVerificationResendForm
          email={email}
          isLoading={resend.isPending}
          isSuccess={resend.isSuccess}
          onEmailChange={setEmail}
          onSubmit={submitResend}
        />
      </div>
    );
  }

  if (confirm.isError) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">
            <FormattedMessage id="auth.emailVerificationConfirm.networkErrorHeading" />
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            <FormattedMessage id="auth.emailVerificationConfirm.networkErrorBody" />
          </p>
        </div>
        <Button type="button" onClick={retry} fullWidth>
          <FormattedMessage id="auth.emailVerificationConfirm.tryAgain" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center space-y-4 py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      <p className="text-sm text-slate-600">
        <FormattedMessage id="auth.emailVerificationConfirm.pending" />
      </p>
    </div>
  );
}
